import { createFileRoute, Link } from '@tanstack/react-router'
import { CreditCard, Info, Lock, Mail, Palette, Receipt } from 'lucide-react'
import { useState } from 'react'
import { useAdminBypass } from '~/app/admin-bypass'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { billingPeriodLabel, planName } from '~/entities/subscription/plans'
import { PlanEndsNote } from '~/entities/subscription/ui/plan-ends-note'
import { CompanyDetailsCard } from '~/features/company-details/company-details-card'
import { ProfileLinkCard } from '~/features/profile-link/components/profile-link-card'
import { CancelSubscriptionDialog } from '~/features/settings/cancel-subscription-dialog'
import { ChangePasswordDialog } from '~/features/settings/change-password-dialog'
import { DeleteAccountDialog } from '~/features/settings/delete-account-dialog'
import { EventEmailsSwitch } from '~/features/settings/event-emails-switch'
import { LanguageSwitch } from '~/features/settings/language-switch'
import { PasswordResetMailInfo } from '~/features/settings/password-reset-mail-info'
import { ResumeSubscriptionDialog } from '~/features/settings/resume-subscription-dialog'
import { ThemeSwitch } from '~/features/settings/theme-switch'
import { TrialSubscriptionDialog } from '~/features/settings/trial-subscription-dialog'
import * as m from '~/paraglide/messages'
import { Alert, AlertDescription, AlertTitle } from '~/shared/ui/alert'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Label } from '~/shared/ui/label'
import { PageHeader } from '~/widgets/page-header'
import { version } from '../../../package.json'

export const Route = createFileRoute('/dashboard/settings')({
  component: Settings,
})

function Settings() {
  const { tier, status, billingPeriod, isTrial, trialEndsAt, pendingChange, isCancelPending } =
    usePlan()
  const { isAdminViewing } = useAdminBypass()
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showMailInfo, setShowMailInfo] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const [showCancelSubscription, setShowCancelSubscription] = useState(false)
  const [showResumeSubscription, setShowResumeSubscription] = useState(false)
  const [showTrial, setShowTrial] = useState(false)

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader title={m.settings()} description={m.manageYourAccountPreferencesAndSettings()} />
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {m.subscription()}
            </CardTitle>
            <CardDescription>{m.chooseThePlanForYourBusiness()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium text-sm">{m.currentPlan()}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={tier === 'free' ? 'secondary' : 'default'}>
                    {planName(tier)}
                  </Badge>
                  {billingPeriod && (
                    <Badge variant="outline">{billingPeriodLabel(billingPeriod)}</Badge>
                  )}
                  {status === 'Active' && (
                    <span className="text-muted-foreground text-xs">{m.planStatusActive()}</span>
                  )}
                </div>
              </div>
            </div>

            <PlanEndsNote className="text-sm" />

            {isTrial && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>{m.trialBannerTitle()}</AlertTitle>
                <AlertDescription>
                  {m.trialBannerDescription({
                    tier: planName(tier),
                    date: trialEndsAt
                      ? new Intl.DateTimeFormat(undefined, {
                          dateStyle: 'medium',
                        }).format(new Date(trialEndsAt))
                      : '-',
                  })}
                  {tier === 'basic' && (
                    <span className="block mt-1">
                      {m.trialUpgradeWarning({
                        tier: planName(tier),
                        targetTier: planName('premium'),
                      })}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2 mt-2">
              <Link to="/dashboard/subscription">
                <Button variant="outline">
                  <CreditCard className="mr-2 w-4 h-4" />
                  {tier === 'free' ? m.upgradePlan() : m.managePlan()}
                </Button>
              </Link>
              {tier !== 'free' && (
                <Button asChild variant="outline">
                  <Link to="/dashboard/subscription" search={{ tab: 'invoices' }}>
                    <Receipt className="mr-2 w-4 h-4" />
                    {m.profileBillingInvoices()}
                  </Link>
                </Button>
              )}
              {tier !== 'free' && status === 'Active' && !isCancelPending && (
                <Button variant="destructive" onClick={() => setShowCancelSubscription(true)}>
                  {m.cancelSubscription()}
                </Button>
              )}
              {pendingChange && (
                <Button variant="outline" onClick={() => setShowResumeSubscription(true)}>
                  {isCancelPending ? m.resumeSubscription() : m.revertScheduledChange()}
                </Button>
              )}
              {isAdminViewing && (
                <Button variant="outline" onClick={() => setShowTrial(true)}>
                  {m.activateTrial()}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <CompanyDetailsCard className="scroll-mt-6" />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              {m.appearance()}
            </CardTitle>
            <CardDescription>{m.manageYourThemeAndLanguagePreferences()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ThemeSwitch />
            <LanguageSwitch />
          </CardContent>
        </Card>

        <ProfileLinkCard className="scroll-mt-6" />

        {!isAdminViewing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                {m.emailsSettingsTitle()}
              </CardTitle>
              <CardDescription>{m.emailsSettingsDescription()}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <EventEmailsSwitch />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {m.security()}
            </CardTitle>
            <CardDescription>{m.manageYourPasswordAndSecuritySettings()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="font-medium text-sm">{m.password()}</Label>
              <p className="mb-2 text-muted-foreground text-sm">••••••••</p>
              <Button variant="outline" onClick={() => setShowChangePassword(true)}>
                <Lock className="mr-2 w-4 h-4" />
                {m.changePassword()}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">{m.dangerZone()}</CardTitle>
            <CardDescription>{m.irreversibleActionsForYourAccount()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="mb-2 font-medium text-sm">{m.deleteAccount()}</h3>
              <p className="mb-4 text-muted-foreground text-sm">
                {m.onceYouDeleteYourAccountThereIsNoGoingBackPleaseBeCertain()}
              </p>
              <Button variant="destructive" onClick={() => setShowDeleteAccount(true)}>
                {m.deleteAccount()}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <ChangePasswordDialog
        open={showChangePassword}
        onOpenChange={setShowChangePassword}
        onCodeSent={(email) => {
          setResetEmail(email)
          setShowMailInfo(true)
        }}
      />
      <PasswordResetMailInfo
        open={showMailInfo}
        onOpenChange={setShowMailInfo}
        email={resetEmail}
      />
      <DeleteAccountDialog open={showDeleteAccount} onOpenChange={setShowDeleteAccount} />
      <CancelSubscriptionDialog
        open={showCancelSubscription}
        onOpenChange={setShowCancelSubscription}
        businessId={businessId}
      />
      <ResumeSubscriptionDialog
        open={showResumeSubscription}
        onOpenChange={setShowResumeSubscription}
        businessId={businessId}
        isCancellation={isCancelPending}
      />
      <TrialSubscriptionDialog
        open={showTrial}
        onOpenChange={setShowTrial}
        businessId={businessId}
      />
      <p className="text-muted-foreground text-xs">
        {m.version()} {version}
      </p>
    </div>
  )
}
